// src/hooks/useLocalStorageState.ts
import { useCallback, useEffect, useState } from "react";

/**
 * A tiny localStorage-backed state hook. Reads on mount, writes on change.
 * Safe for SSR (returns `defaultValue` if `window` is undefined).
 *
 * @param key Stable storage key. Include any identifying ids (e.g. projectId).
 * @param defaultValue Returned when key is missing or JSON.parse fails.
 */
export function useLocalStorageState<T>(
  key: string,
  defaultValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === "undefined") return defaultValue;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return defaultValue;
      return JSON.parse(raw) as T;
    } catch {
      return defaultValue;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Storage full / disabled — best-effort only.
    }
  }, [key, value]);

  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) =>
        typeof next === "function" ? (next as (p: T) => T)(prev) : next,
      );
    },
    [],
  );

  return [value, set];
}
